import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { adminApi } from '../../services/adminApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ServicePageModal from '../../components/admin/ServicePageModal';
import AdminLayout from '../../components/admin/AdminLayout';
import { renderCountryFlag } from '../../utils/flagUtils';
import { formatDateTime } from '../../utils/timezoneUtils';

interface ServicePage {
  id: number;
  slug: string;
  status: 'draft' | 'published' | 'archived';
  display_order: number;
  menu_position: 'main' | 'services' | 'footer' | 'none';
  is_featured: boolean;
  template_type: string;
  created_at: string;
  updated_at: string;
  country_id: number | null;
  country_name: string | null;
  country_name_ar: string | null;
  country_code: string | null;
  flag_emoji: string | null;
  hero_title_en: string | null;
  hero_title_ar: string | null;
  meta_title_en: string | null;
  meta_title_ar: string | null;
}

interface PaginationInfo {
  current_page: number;
  total_pages: number;
  total_items: number;
  items_per_page: number;
}

interface Country {
  id: number;
  name: string;
  name_ar: string;
  code: string;
  flag_emoji: string;
}

const ServicePageManagement: React.FC = () => {
  const { } = useTranslation();
  const [pages, setPages] = useState<ServicePage[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    country_id: '',
    search: '',
    page: 1
  });
  const [selectedPage, setSelectedPage] = useState<ServicePage | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPage, setEditingPage] = useState<ServicePage | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);

  useEffect(() => {
    fetchServicePages();
    fetchCountries();
  }, [filters]);

  const fetchServicePages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filters.status) params.append('status', filters.status);
      if (filters.country_id) params.append('country_id', filters.country_id);
      if (filters.search) params.append('search', filters.search);
      params.append('page', filters.page.toString());
      params.append('limit', '20');

      const response = await adminApi.get(`/admin/service-pages/?${params.toString()}`) as {
        success: boolean;
        data: {
          pages: ServicePage[];
          pagination: PaginationInfo;
        };
      };
      setPages(response.data?.pages || []);
      setPagination(response.data?.pagination || {
        current_page: 1,
        total_pages: 1,
        total_items: 0,
        items_per_page: 20
      });
      setError(null);
    } catch (err: any) {      setError(err.response?.data?.message || 'Failed to fetch service pages');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await adminApi.countries.getAll() as {
        success: boolean;
        countries: Country[];
      };
      setCountries(response.countries || []);
    } catch (err: any) {    }
  };

  const handleDeletePage = async (page: ServicePage) => {
    try {
      await adminApi.delete(`/admin/service-pages?id=${page.id}`);
      setPages(pages.filter(p => p.id !== page.id));
      setShowDeleteModal(false);
      setSelectedPage(null);
    } catch (err: any) {      setError(err.response?.data?.message || 'Failed to delete service page');
    }
  };

  const handleCreatePage = () => {
    setEditingPage(null);
    setShowModal(true);
  };

  const handleEditPage = (page: ServicePage) => {
    setEditingPage(page);
    setShowModal(true);
  };

  const handleSavePage = async (pageData: any) => {
    try {
      if (editingPage) {
        // Update existing page
        await adminApi.put(`/admin/service-pages?id=${editingPage.id}`, pageData);
        // Refresh the list
        fetchServicePages();
      } else {
        // Create new page
        await adminApi.post('/admin/service-pages', pageData);
        // Refresh the list
        fetchServicePages();
      }
      setShowModal(false);
      setEditingPage(null);
    } catch (err: any) {      throw err; // Re-throw to let modal handle the error
    }
  };

  const handleStatusChange = async (page: ServicePage, newStatus: string) => {
    try {
      await adminApi.put(`/admin/service-pages?id=${page.id}`, {
        status: newStatus
      });

      setPages(pages.map(p =>
        p.id === page.id ? { ...p, status: newStatus as any } : p
      ));
    } catch (err: any) {      setError(err.response?.data?.message || 'Failed to update page status');
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800 border-green-200';
      case 'draft': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'archived': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getMenuPositionBadge = (position: string) => {
    const positions = {
      main: { label: 'Main Menu', color: 'bg-blue-100 text-blue-800 border-blue-200' },
      services: { label: 'Services', color: 'bg-purple-100 text-purple-800 border-purple-200' },
      footer: { label: 'Footer', color: 'bg-gray-100 text-gray-800 border-gray-200' },
      none: { label: 'Not in Menu', color: 'bg-red-100 text-red-800 border-red-200' }
    };

    const pos = positions[position as keyof typeof positions] || positions.none;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${pos.color}`}>
        {pos.label}
      </span>
    );
  };

  const filteredAndSortedPages = (pages || []).sort((a, b) => {
    if (a.display_order !== b.display_order) {
      return a.display_order - b.display_order;
    }
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  if (loading && (!pages || pages.length === 0)) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Service Pages Management</h1>
            <p className="mt-2 text-gray-600">
              Manage multilingual service pages and their content
            </p>
          </div>
          <button
            onClick={handleCreatePage}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
          >
            + Create New Page
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
              placeholder="Search by title, country..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Country Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <select
              value={filters.country_id}
              onChange={(e) => setFilters({ ...filters, country_id: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Countries</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.flag_emoji || '🌍'} {country.name}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: '', country_id: '', search: '', page: 1 })}
              className="w-full px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        </div>
      )}

      {/* Service Pages Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Page</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Country</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Status</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Menu Position</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Order</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Updated</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <AnimatePresence>
                {filteredAndSortedPages.map((page) => (
                  <motion.tr
                    key={page.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="py-4 px-6">
                      <div>
                        <div className="font-semibold text-gray-900 flex items-center">
                          {page.hero_title_en || page.hero_title_ar || page.slug}
                          {page.is_featured && (
                            <span className="ml-2 inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                              ⭐ Featured
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          <code className="bg-gray-100 px-2 py-1 rounded text-xs">/{page.slug}</code>
                        </div>
                        {(page.hero_title_en || page.hero_title_ar) && (
                          <div className="text-xs text-gray-500 mt-1">
                            {page.hero_title_en && <div>🇬🇧 {page.hero_title_en}</div>}
                            {page.hero_title_ar && <div>🇸🇦 {page.hero_title_ar}</div>}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {page.country_name ? (
                        <div className="flex items-center">
                          <div className="mr-2">
                            {renderCountryFlag(
                              page.country_code || '',
                              page.flag_emoji || '🌍',
                              page.country_name,
                              'w-8 h-6 object-cover rounded-sm'
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{page.country_name}</div>
                            {page.country_name_ar && (
                              <div className="text-sm text-gray-500">{page.country_name_ar}</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">No Country</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <select
                        value={page.status}
                        onChange={(e) => handleStatusChange(page, e.target.value)}
                        className={`text-xs font-medium border rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 ${getStatusBadgeColor(page.status)}`}
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="archived">Archived</option>
                      </select>
                    </td>
                    <td className="py-4 px-6">
                      {getMenuPositionBadge(page.menu_position)}
                    </td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                        {page.display_order}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-500">
                      {formatDateTime(page.updated_at, { year: 'numeric', month: 'short', day: 'numeric' }, 'en-US')}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        {/* Edit Button */}
                        <button
                          onClick={() => handleEditPage(page)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-full transition-colors duration-200"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>

                        {/* Preview Button */}
                        <button
                          onClick={() => {
                            window.open(`/services/${page.slug}`, '_blank');
                          }}
                          className="text-green-600 hover:text-green-800 hover:bg-green-50 p-2 rounded-full transition-colors duration-200"
                          title="Preview"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => {
                            setSelectedPage(page);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-full transition-colors duration-200"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredAndSortedPages.length === 0 && !loading && (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No service pages found</h3>
            <p className="text-gray-500 mb-6">Get started by creating your first service page.</p>
            <button
              onClick={handleCreatePage}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Create Service Page
            </button>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Showing {((pagination.current_page - 1) * pagination.items_per_page) + 1} to{' '}
            {Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)} of{' '}
            {pagination.total_items} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              disabled={pagination.current_page <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {[...Array(pagination.total_pages)].map((_, index) => (
              <button
                key={index}
                onClick={() => setFilters({ ...filters, page: index + 1 })}
                className={`px-3 py-2 text-sm font-medium rounded-md ${
                  pagination.current_page === index + 1
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {index + 1}
              </button>
            ))}
            <button
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              disabled={pagination.current_page >= pagination.total_pages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Service Page Create/Edit Modal */}
      <ServicePageModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingPage(null);
        }}
        page={editingPage}
        onSave={handleSavePage}
        countries={countries}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Service Page</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedPage.hero_title_en || selectedPage.slug}"?
              This action cannot be undone and will remove all associated content and sections.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedPage(null);
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => selectedPage && handleDeletePage(selectedPage)}
                className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default ServicePageManagement;